import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Package, ArrowLeft, Check, Search, Lock } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { NurseryTreeType, NurseryStockItem, CreateStockItemPayload } from '../../types/nursery';

const stockItemSchema = z.object({
  cultivar_id: z.number({ message: 'Cultivar is required' }).min(1, 'Cultivar is required'),
  rootstock_id: z.number().optional(),
  tree_type_id: z.number({ message: 'Tree type is required' }).min(1, 'Tree type is required'),
  sku_code: z.string().optional(),
  barcode: z.string().max(30, 'Keep the barcode to 30 characters').optional(),
  description: z.string().optional(),
  website_price: z.number({ message: 'Enter a valid price' }).min(0, 'Must be 0 or greater').optional(),
});

type StockItemFormData = z.infer<typeof stockItemSchema>;

// Empty number inputs/selects come back as '' — coerce to undefined so the
// optional() branch of the zod schema applies instead of failing on NaN.
const optionalNumber = (v: unknown) =>
  v === '' || v === null || v === undefined ? undefined : Number(v);

function StockItemForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  // Stock item (SKU) catalogue management requires the nursery.admin tier.
  // All hooks must run before any early return (see CLAUDE.md hooks-first rule).
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', 'admin');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(true);

  // Tree types
  const [treeTypes, setTreeTypes] = useState<NurseryTreeType[]>([]);

  // Cultivar search
  const [cultivarQuery, setCultivarQuery] = useState('');
  const [cultivarResults, setCultivarResults] = useState<any[]>([]);
  const [selectedCultivar, setSelectedCultivar] = useState<any | null>(null);

  // Rootstock search
  const [rootstockQuery, setRootstockQuery] = useState('');
  const [rootstockResults, setRootstockResults] = useState<any[]>([]);
  const [selectedRootstock, setSelectedRootstock] = useState<any | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<StockItemFormData>({
    resolver: zodResolver(stockItemSchema),
  });

  // Load tree types
  useEffect(() => {
    nurseryService.getTreeTypes().then(res => {
      if (res.success) setTreeTypes(res.data || []);
    });
  }, []);

  // Cultivars this nursery is licensed to propagate (active nursery_propagation
  // licences) — the stock-item picker is restricted to these, NOT the global
  // registry. Loaded once, then filtered client-side as the user types.
  const [licensedCultivars, setLicensedCultivars] = useState<any[]>([]);
  const [licensedLoaded, setLicensedLoaded] = useState(false);

  useEffect(() => {
    nurseryService.getLicensedCultivars().then(res => {
      if (res.success) setLicensedCultivars(res.data || []);
      setLicensedLoaded(true);
    }).catch(() => setLicensedLoaded(true));
  }, []);

  // Existing item (edit mode). The joined cultivar/rootstock names are used to
  // seed the pickers so the user sees what is selected without re-searching.
  useEffect(() => {
    if (!isEdit) return;
    nurseryService.getStockItem(Number(id)).then(res => {
      if (res.success && res.data) {
        const s: NurseryStockItem = res.data;
        const price = s.website_price == null ? undefined : Number(s.website_price);
        reset({
          cultivar_id: s.cultivar_id,
          rootstock_id: s.rootstock_id ?? undefined,
          tree_type_id: s.tree_type_id,
          sku_code: s.sku_code ?? undefined,
          barcode: s.barcode ?? undefined,
          description: s.description ?? undefined,
          website_price: price != null && !Number.isNaN(price) ? price : undefined,
        });
        setActive(s.active);
        setSelectedCultivar({
          id: s.cultivar_id,
          cultivar_name: s.cultivar_name,
          trade_name: s.cultivar_trade_name,
        });
        if (s.rootstock_id) {
          setSelectedRootstock({ id: s.rootstock_id, cultivar_name: s.rootstock_name });
        }
      } else {
        setError('Stock item not found');
      }
      setLoading(false);
    }).catch(e => {
      setError(e instanceof Error ? e.message : 'Failed to load stock item');
      setLoading(false);
    });
  }, [id, isEdit, reset]);

  useEffect(() => {
    if (cultivarQuery.length < 1) { setCultivarResults([]); return; }
    const q = cultivarQuery.toLowerCase();
    setCultivarResults(
      licensedCultivars.filter(c =>
        c.cultivar_role !== 'rootstock' && (
        (c.cultivar_name || '').toLowerCase().includes(q) ||
        (c.trade_name || '').toLowerCase().includes(q)),
      ).slice(0, 10),
    );
  }, [cultivarQuery, licensedCultivars]);

  // Rootstock search — the same propagable list as the scion picker (open, or
  // held under a licence), narrowed to cultivars marked rootstock or dual on
  // the cultivar page. A PBR-protected rootstock this nursery holds no licence
  // for is not offered.
  useEffect(() => {
    if (rootstockQuery.length < 1) { setRootstockResults([]); return; }
    const q = rootstockQuery.toLowerCase();
    setRootstockResults(
      licensedCultivars.filter(c =>
        (c.cultivar_role === 'rootstock' || c.cultivar_role === 'dual') && (
        (c.cultivar_name || '').toLowerCase().includes(q) ||
        (c.trade_name || '').toLowerCase().includes(q)),
      ).slice(0, 10),
    );
  }, [rootstockQuery, licensedCultivars]);

  const handleSelectCultivar = (cultivar: any) => {
    setSelectedCultivar(cultivar);
    setValue('cultivar_id', cultivar.id);
    setCultivarQuery('');
    setCultivarResults([]);
  };

  const handleSelectRootstock = (rootstock: any) => {
    setSelectedRootstock(rootstock);
    setValue('rootstock_id', rootstock.id);
    setRootstockQuery('');
    setRootstockResults([]);
  };

  const onSubmit = async (data: StockItemFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      const price = data.website_price != null && !Number.isNaN(data.website_price)
        ? data.website_price
        : null;

      if (isEdit) {
        // Send explicit nulls so a cleared rootstock / price / SKU is actually unset.
        const res = await nurseryService.updateStockItem(Number(id), {
          cultivar_id: data.cultivar_id,
          tree_type_id: data.tree_type_id,
          rootstock_id: data.rootstock_id ?? null,
          sku_code: data.sku_code?.trim() || null,
          barcode: data.barcode?.replace(/\s+/g, '') || null,
          description: data.description?.trim() || null,
          website_price: price,
          active,
        });
        if (res.success) {
          navigate('/nursery/stock-items');
        } else {
          setError((res as any).error?.message || 'Failed to save stock item');
        }
        return;
      }

      const payload: CreateStockItemPayload = {
        cultivar_id: data.cultivar_id,
        tree_type_id: data.tree_type_id,
        ...(data.rootstock_id ? { rootstock_id: data.rootstock_id } : {}),
        ...(data.sku_code ? { sku_code: data.sku_code } : {}),
        ...(data.barcode?.trim() ? { barcode: data.barcode.replace(/\s+/g, '') } : {}),
        ...(data.description ? { description: data.description } : {}),
        ...(price != null ? { website_price: price } : {}),
      };
      const res = await nurseryService.createStockItem(payload);
      if (res.success) {
        navigate('/nursery/stock-items');
      } else {
        setError((res as any).error?.message || 'Failed to create stock item');
      }
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  };

  if (licenseLoading || loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Admin license required</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Creating and editing stock items (SKUs) is part of the nursery catalogue
            setup and requires an active <span className="font-medium">Nursery Admin</span> licence seat.
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery/stock-items')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to stock items
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Edit Stock Item' : 'New Stock Item'}
        icon={<Package className="h-6 w-6 text-white" />}
      />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
            {/* Cultivar picker — restricted to licensed cultivars */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Cultivar <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-neutral-400 mb-1">
                Only cultivars your nursery is licensed to propagate are shown.
              </p>
              {selectedCultivar ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-green-800">
                    {selectedCultivar.cultivar_name || selectedCultivar.name}
                    {selectedCultivar.trade_name && ` (${selectedCultivar.trade_name})`}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedCultivar(null); setValue('cultivar_id', 0); }}
                    className="text-green-600 hover:text-green-800 ml-auto"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <input
                      type="text"
                      value={cultivarQuery}
                      onChange={e => setCultivarQuery(e.target.value)}
                      placeholder="Search your licensed cultivars..."
                      className="w-full border border-neutral-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  {cultivarResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {cultivarResults.map((c: any) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleSelectCultivar(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                        >
                          <span className="font-medium">{c.cultivar_name || c.name}</span>
                          {c.trade_name && <span className="text-neutral-400 ml-1">({c.trade_name})</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {!licensedLoaded && (
                    <p className="text-xs text-neutral-400 mt-1">Loading licensed cultivars...</p>
                  )}
                  {licensedLoaded && licensedCultivars.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Your nursery has no active propagation licences, so there are no cultivars available.
                      Stock items can only be created for cultivars you are licensed to grow.
                    </p>
                  )}
                  {licensedLoaded && licensedCultivars.length > 0 && cultivarQuery.length >= 1 && cultivarResults.length === 0 && (
                    <p className="text-xs text-neutral-400 mt-1">No licensed cultivars match.</p>
                  )}
                </div>
              )}
              {errors.cultivar_id && (
                <p className="text-xs text-red-500 mt-1">{errors.cultivar_id.message}</p>
              )}
            </div>

            {/* Rootstock search (optional) */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Rootstock</label>
              {selectedRootstock ? (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <span className="text-sm font-medium text-blue-800">
                    {selectedRootstock.cultivar_name || selectedRootstock.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedRootstock(null); setValue('rootstock_id', undefined); }}
                    className="text-blue-600 hover:text-blue-800 ml-auto"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <input
                      type="text"
                      value={rootstockQuery}
                      onChange={e => setRootstockQuery(e.target.value)}
                      placeholder="Search rootstock (optional)..."
                      className="w-full border border-neutral-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  {rootstockResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {rootstockResults.map((r: any) => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => handleSelectRootstock(r)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                        >
                          <span className="font-medium">{r.cultivar_name || r.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {rootstockQuery && rootstockResults.length === 0 && licensedLoaded && (
                    <p className="text-xs text-neutral-400 mt-1">
                      No open or licensed rootstock matches. Protected rootstocks need a propagation licence.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Tree type */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Tree Type <span className="text-red-500">*</span>
              </label>
              <select
                {...register('tree_type_id', { setValueAs: optionalNumber })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select tree type...</option>
                {treeTypes.filter(tt => tt.is_active).map(tt => (
                  <option key={tt.id} value={tt.id}>
                    {tt.name} ({tt.code}) - {tt.stock_type === 'B' ? 'Bareroot' : 'Container'}
                  </option>
                ))}
              </select>
              {errors.tree_type_id && (
                <p className="text-xs text-red-500 mt-1">{errors.tree_type_id.message}</p>
              )}
            </div>

            {/* SKU */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">SKU Code</label>
              <input
                type="text"
                {...register('sku_code')}
                placeholder="e.g. FJ-M9-BR"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Barcode */}
            <div>
              <label htmlFor="stock-item-barcode" className="block text-sm font-medium text-neutral-700 mb-1">Barcode</label>
              <input
                id="stock-item-barcode"
                type="text"
                inputMode="numeric"
                {...register('barcode')}
                placeholder="e.g. 9358210298306"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.barcode
                ? <p className="text-xs text-red-500 mt-1">{errors.barcode.message}</p>
                : (
                  <p className="text-xs text-neutral-500 mt-1">
                    A retailer's purchase order names the tree by it. Leave it blank when the tree is sold under a product, and
                    put the barcode on the product.
                  </p>
                )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
              <textarea
                {...register('description')}
                rows={3}
                placeholder="Additional details about this stock item..."
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>

            {/* Website price */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Website Price (AUD)</label>
              <p className="text-xs text-neutral-400 mb-1">
                Set a price to make this item orderable on the public website (only for published cultivars). Leave blank to keep it offline.
              </p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('website_price', { setValueAs: optionalNumber })}
                  placeholder="e.g. 45.50"
                  className="w-full border border-neutral-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              {errors.website_price && (
                <p className="text-xs text-red-500 mt-1">{errors.website_price.message}</p>
              )}
            </div>

            {/* Active — edit only. Inactive SKUs drop out of the catalogue and the
                stock item list without being deleted. */}
            {isEdit && (
              <div className="pt-1">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={e => setActive(e.target.checked)}
                    className="rounded border-neutral-300"
                  />
                  Active
                </label>
                <p className="text-xs text-neutral-400 mt-1">
                  Uncheck to retire this SKU. It stays on historical orders but is hidden from the catalogue.
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" type="button" onClick={() => navigate('/nursery/stock-items')}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Stock Item'}
              {!submitting && <Check className="h-4 w-4" />}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

export default StockItemForm;
